/**
 * Per-PAGE admin-shell guided tours (EPIC #1952 Wave 2, issue #1955).
 *
 * Where the admin SECTION tours (``adminSectionTours.ts``) walk a whole nav
 * GROUP from Training, these are focused, single-PAGE deep-dives wired to the
 * route via ``routeTours.ts`` so the header launcher (the GraduationCap button)
 * starts them on the page in focus. One short tour per genuinely COMPLEX admin
 * page -- a page with filters + summary tiles + a drill-in table, a multi-mode
 * editor, or a guarded workflow. Genuinely WYSIWYG pages (a flat list / single
 * form) get NO tour and are documented as exclusions in the PR; the launcher is
 * simply absent there (hidden-when-no-tour).
 *
 * Conventions (mirror adminSectionTours.ts):
 *  - Copy (D4-ONB-011 / D4-WLB): agency-neutral via the ``{agency}`` token, plain
 *    professional language, NO emojis, never a hardcoded agency/vendor/location
 *    name (the D4-WLB-008 grep guard stays green).
 *  - Gating (D4-ONB-004): a money step gates on ``module: "financial"`` and a
 *    FICA step on ``module: "fica"`` -- exactly the admin sidebar's ``itemVisible``
 *    rule (``adminNav.ts``). Admins hold every permission flag, so these tours
 *    gate on MODULE only, never permFlag.
 *  - Audience (#1347): every tour declares ``audience: { adminOnly: true }`` so a
 *    plain agent (whose ungated ``center`` first step would otherwise pass) is
 *    never offered an admin-shell tour.
 *  - Targets are stable ``data-tour="..."`` anchors on REAL, visible elements of
 *    the page. The opening step is always a ``center`` narrative OR an
 *    always-rendered ``page-admin-*`` anchor, so the tour starts standalone even
 *    while the page is still loading (the engine skips a momentarily-absent
 *    target).
 *  - Every step's route stays inside the admin shell (``/admin/...``).
 *
 * REQs: D4-ONB-008 (per-surface admin tours), D4-ONB-011 (agency-neutral copy).
 */
import type { TourDefinition, TourStep } from "./types";
import { BACK_OFFICE_AGENT_COMPLIANCE_PAGE_TOUR } from "./agentPageTours";

/**
 * Commission dashboard (``/admin/commission``) -- what the agency has earned,
 * been paid and is still owed, agent by agent. A money page, so the whole tour
 * gates on the financial module via its steps.
 */
export const ADMIN_COMMISSION_PAGE_TOUR: TourDefinition = {
  id: "admin-commission-page",
  title: "Commission dashboard tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/commission",
      target: "page-admin-commission",
      section: "Welcome",
      module: "financial",
      title: "Company commission",
      body: "This is your company commission view -- what the agency has earned, what has been paid and what is still outstanding, agent by agent. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "controls",
      route: "/admin/commission",
      target: "admin-commission-controls",
      section: "Controls",
      module: "financial",
      title: "Pick a year, filter and export",
      body: "Choose a financial year, open Filters to narrow by payment status or date range, and export the figures for your records. The page refreshes to match your selection.",
      placement: "bottom",
    },
    {
      id: "table",
      route: "/admin/commission",
      target: "admin-commission-table",
      section: "Deals",
      module: "financial",
      title: "Every commission, deal by deal",
      body: "The table lists each deal's commission -- earned, paid and outstanding. You can mark a deal as paid against an invoice from here. That is your commission dashboard.",
      placement: "top",
    },
  ],
};

/**
 * Sales Targets (``/admin/sales-targets``) -- registered vs agent target vs company
 * target, per area, team and agent. No money module gate (targets are visible
 * regardless), but admin-only.
 */
export const ADMIN_SALES_TARGETS_TOUR: TourDefinition = {
  id: "admin-sales-targets-page",
  title: "Sales Targets tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/sales-targets",
      target: "page-admin-sales-targets",
      section: "Welcome",
      title: "Tracking your targets",
      body: "Sales Targets shows registered sales against each agent's target and the company target, per area, team and agent. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "header",
      route: "/admin/sales-targets",
      target: "admin-sales-targets-header",
      section: "Add a target",
      title: "Set a new target",
      body: "Use New target to set a turnover target for an agent, a team, an area or the whole company for a financial year. Refresh re-pulls the latest registered and written figures.",
      placement: "bottom",
    },
    {
      id: "filters",
      route: "/admin/sales-targets",
      target: "admin-sales-targets-filters",
      section: "Filters",
      title: "Focus the view",
      body: "Pick the financial year and, if you like, narrow to one agent or one area. The targets below update to match. That is Sales Targets.",
      placement: "bottom",
    },
  ],
};

/**
 * Leaderboard (``/admin/leaderboard``) -- agent rankings by turnover, target %
 * and active mandates, with analysis filters.
 */
export const ADMIN_LEADERBOARD_TOUR: TourDefinition = {
  id: "admin-leaderboard-page",
  title: "Leaderboard tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/leaderboard",
      target: "page-admin-leaderboard",
      section: "Welcome",
      title: "Your leaderboard",
      body: "The Leaderboard ranks your agents by turnover, their percentage to the annual target and their active mandates. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "header",
      route: "/admin/leaderboard",
      target: "admin-leaderboard-header",
      section: "Filters",
      title: "Filter the period",
      body: "Open Filters to set the date range and, where activity scores are present, to narrow by KPI tier or by who met their activity target.",
      placement: "bottom",
    },
    {
      id: "summary",
      route: "/admin/leaderboard",
      target: "admin-leaderboard-summary",
      section: "Summary",
      title: "The headline numbers",
      body: "These cards summarise total turnover, total deals and how many agents have deals, all reflecting the filters you have set.",
      placement: "bottom",
    },
    {
      id: "table",
      route: "/admin/leaderboard",
      target: "admin-leaderboard-table",
      section: "Rankings",
      title: "The rankings",
      body: "The ranked table is your at-a-glance view of who is leading and who needs a nudge. That is your leaderboard.",
      placement: "top",
    },
  ],
};

/**
 * Monthly Deal Summary (``/admin/monthly-deals``) -- new, registered and lease
 * deals by period, filterable.
 */
export const ADMIN_MONTHLY_DEALS_TOUR: TourDefinition = {
  id: "admin-monthly-deals-page",
  title: "Monthly deals tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/monthly-deals",
      target: "page-admin-monthly-deals",
      section: "Welcome",
      title: "Your month at a glance",
      body: "The Monthly Deal Summary rolls up new deals, registered deals and leases for a period, so you can see how the agency performed. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "header",
      route: "/admin/monthly-deals",
      target: "admin-monthly-deals-header",
      section: "Overview",
      title: "New, registered and leases",
      body: "Each section groups the period's deals by type so the new, registered and lease activity sit side by side.",
      placement: "bottom",
    },
    {
      id: "filters",
      route: "/admin/monthly-deals",
      target: "admin-monthly-deals-filters",
      section: "Filters",
      title: "Choose the period",
      body: "Set the date range and, if you like, narrow by agent, area or deal type. The summary updates to match. That is your monthly summary.",
      placement: "bottom",
    },
  ],
};

/**
 * Market Share (``/admin/market-share``) -- registered deals by area, with
 * year and trend comparison.
 */
export const ADMIN_MARKET_SHARE_TOUR: TourDefinition = {
  id: "admin-market-share-page",
  title: "Market Share tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/market-share",
      target: "page-admin-market-share",
      section: "Welcome",
      title: "Where you are strong",
      body: "Market Share shows your registered deals by area, so you can see where the agency is strong and where to push. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "header",
      route: "/admin/market-share",
      target: "admin-market-share-header",
      section: "Registered deals",
      title: "Registered, by area",
      body: "These are registered deals -- sales that have reached transfer at the Deeds Office -- counted and valued by area, not pending written sales.",
      placement: "bottom",
    },
    {
      id: "years",
      route: "/admin/market-share",
      target: "admin-market-share-years",
      section: "Compare years",
      title: "Compare across years",
      body: "Pick a year, or select several to compare the trend across financial years. The breakdown below follows your choice. That is Market Share.",
      placement: "bottom",
    },
  ],
};

/**
 * Deal Pipeline (``/admin/deal-pipeline``) -- outstanding deals in the
 * conveyancing process, with stage tiles, filters and a drill-in table.
 */
export const ADMIN_DEAL_PIPELINE_TOUR: TourDefinition = {
  id: "admin-deal-pipeline-page",
  title: "Deal Pipeline tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/deal-pipeline",
      target: "page-admin-deal-pipeline",
      section: "Welcome",
      title: "Your live pipeline",
      body: "The Deal Pipeline tracks every deal still in the conveyancing process, so nothing stalls unnoticed. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "summary",
      route: "/admin/deal-pipeline",
      target: "admin-deal-pipeline-summary",
      section: "Stages",
      title: "Stage tiles",
      body: "These tiles count deals at each stage and their value. Click a stage to filter the list below to just those deals.",
      placement: "bottom",
    },
    {
      id: "filters",
      route: "/admin/deal-pipeline",
      target: "admin-deal-pipeline-filters",
      section: "Filters",
      title: "Narrow the list",
      body: "Filter the pipeline by agent, area, attorney or date range to focus on the deals you care about right now.",
      placement: "bottom",
    },
    {
      id: "table",
      route: "/admin/deal-pipeline",
      target: "center",
      section: "Deals",
      title: "Open a deal",
      body: "The list below shows each outstanding deal with its urgency and status. Click any row to open the deal and see its full progress. That is your pipeline.",
      placement: "center",
    },
  ],
};

/**
 * Attorney Pipeline (``/admin/attorney-pipeline``) -- deal values grouped by
 * conveyancing firm, with bond-source filtering.
 */
export const ADMIN_ATTORNEY_PIPELINE_TOUR: TourDefinition = {
  id: "admin-attorney-pipeline-page",
  title: "Attorney Pipeline tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/attorney-pipeline",
      target: "page-admin-attorney-pipeline",
      section: "Welcome",
      title: "By conveyancing firm",
      body: "The Attorney Pipeline groups your deals by conveyancing firm, so you can see who is sitting on transfers that should be registering. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "header",
      route: "/admin/attorney-pipeline",
      target: "admin-attorney-pipeline-header",
      section: "Filters",
      title: "Filter and refresh",
      body: "Pick the date basis -- the date the sale was written, or the registration date -- then open Filters to narrow by date, deal type or bond source. Use Refresh to re-pull the latest values. Each firm's deals are grouped below.",
      placement: "bottom",
    },
    {
      id: "groups",
      route: "/admin/attorney-pipeline",
      target: "center",
      section: "Firms",
      title: "Each firm's deals",
      body: "Below, every conveyancing firm shows its deals and totals, so a firm holding things up is easy to spot. That is the Attorney Pipeline.",
      placement: "center",
    },
  ],
};

/**
 * KPI Reports (``/admin/kpis``) -- company-wide agent KPI performance for a
 * chosen month, with column controls.
 */
export const ADMIN_KPI_REPORT_TOUR: TourDefinition = {
  id: "admin-kpi-report-page",
  title: "KPI Reports tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/kpis",
      target: "page-admin-kpi-report",
      section: "Welcome",
      title: "Your KPI scorecard",
      body: "KPI Reports score every agent on the activity that drives sales -- viewings, valuations and the rest -- company-wide, for any month you choose. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "bottom",
    },
    {
      id: "controls",
      route: "/admin/kpis",
      target: "admin-kpi-report-controls",
      section: "Controls",
      title: "Month and columns",
      body: "Step the month back and forth, choose which columns to show, and print or export the scorecard for a sales meeting.",
      placement: "bottom",
    },
    {
      id: "config",
      route: "/admin/kpis",
      target: "page-admin-kpi-report",
      section: "Configure",
      title: "Tune the scorecard",
      body: "The metrics, points and tiers behind these scores are yours to set -- under Settings you can edit the KPI metrics and the monthly tier targets, and the next score follows your changes. That is KPI Reports.",
      placement: "center",
    },
  ],
};

/**
 * KPI Metrics editor (``/admin/kpi-metrics``) -- the catalogue of metrics,
 * points and targets that make up the scorecard.
 */
export const ADMIN_KPI_METRICS_TOUR: TourDefinition = {
  id: "admin-kpi-metrics-page",
  title: "KPI Metrics tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/kpi-metrics",
      target: "page-admin-kpi-metrics",
      section: "Welcome",
      title: "Define your metrics",
      body: "KPI Metrics is where you decide which activities count, how many points each one earns and the monthly target -- the recipe behind every agent's score. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "header",
      route: "/admin/kpi-metrics",
      target: "admin-kpi-metrics-header",
      section: "Catalogue",
      title: "Your metric catalogue",
      body: "Each metric carries a name, category, points weight and target. Edit one and the change flows into the next score automatically -- a scoring change is staged as a draft you publish when ready, so a closed month is never rewritten.",
      placement: "bottom",
    },
    {
      id: "new",
      route: "/admin/kpi-metrics",
      target: "admin-kpi-metrics-new",
      kind: "action",
      section: "Add a metric",
      title: "Add a metric",
      body: "New Metric adds your own measure. Auto-tracked metrics are calculated for you; the rest are captured by hand. You can also restore the recommended defaults at any time. That is KPI Metrics.",
      placement: "left",
    },
  ],
};

/**
 * KPI Tiers (``/admin/kpi-tiers``) -- per-tier monthly activity-score targets
 * and agent assignment (or, for a single-tier agency, one company-wide target).
 */
export const ADMIN_KPI_TIERS_TOUR: TourDefinition = {
  id: "admin-kpi-tiers-page",
  title: "KPI Tiers tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/kpi-tiers",
      target: "page-admin-kpi-tiers",
      section: "Welcome",
      title: "Set the bar",
      body: "KPI Tiers set the monthly activity-score target each agent is measured against. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "header",
      route: "/admin/kpi-tiers",
      target: "admin-kpi-tiers-header",
      section: "Tiers",
      title: "One bar, or several",
      body: "Set a single company-wide target for everyone, or create tiers -- each with its own monthly target -- and assign your agents so each is measured against the right bar. Agents with no tier use the default.",
      placement: "bottom",
    },
    {
      id: "scores",
      route: "/admin/kpi-tiers",
      target: "page-admin-kpi-tiers",
      section: "Scores",
      title: "Where the scores come from",
      body: "These targets are the bar each agent's monthly KPI score is judged against -- the score itself is built from the metrics you set under KPI Metrics. That is KPI Tiers.",
      placement: "center",
    },
  ],
};

/**
 * FICA Compliance dashboard (``/admin/compliance``) -- verification status,
 * document expiry and high-risk cases. FICA module-gated.
 */
export const ADMIN_FICA_PAGE_TOUR: TourDefinition = {
  id: "admin-fica-page",
  title: "FICA Compliance tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/compliance",
      target: "center",
      section: "Welcome",
      module: "fica",
      title: "Keeping FICA clean",
      body: "The FICA dashboard tracks verification status, document expiry and high-risk cases across every deal, so nothing registers without the paperwork being right. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "overview",
      route: "/admin/compliance",
      target: "page-admin-fica",
      section: "Overview",
      module: "fica",
      title: "Your overview",
      body: "The cards at the top count your cases by status -- verified, pending, expired and high-risk -- with the month's verification cost, so the health of your compliance is one glance.",
      placement: "bottom",
    },
    {
      id: "tabs",
      route: "/admin/compliance",
      target: "admin-fica-tabs",
      section: "Cases",
      module: "fica",
      title: "Work the cases",
      body: "These tabs hold everything FICA -- the case list and high-risk queue, the audit log, training, costs and the monthly report, the agent scorecard, your risk-management programme (RMCP), regulator (RCR) data, ongoing monitoring and your verification providers. Scroll the row to see them all. Filter the cases and open any one to review its documents. That is FICA Compliance.",
      placement: "bottom",
    },
  ],
};

/**
 * Communications (``/admin/communications``) -- company-wide message history
 * and delivery health.
 */
export const ADMIN_COMMUNICATIONS_TOUR: TourDefinition = {
  id: "admin-communications-page",
  title: "Communications tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/communications",
      target: "page-admin-communications",
      section: "Welcome",
      title: "Every message, one place",
      body: "Communications is the company-wide history of every email and WhatsApp the agency has sent, plus how well they are being delivered. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "bottom",
    },
    {
      id: "tabs",
      route: "/admin/communications",
      target: "admin-communications-tabs",
      section: "Tabs",
      title: "History and health",
      body: "Message History is the searchable log of every send; Delivery Health surfaces bounces and failures so you can act before messages stop landing.",
      placement: "bottom",
    },
    {
      id: "filters",
      route: "/admin/communications",
      target: "page-admin-communications",
      section: "Filter",
      title: "Find a message",
      body: "Filter by agent, channel, direction, status or date range to find any message fast, then triage anything that failed. That is Communications.",
      placement: "center",
    },
  ],
};

/**
 * User Management (``/admin/users``) -- invite agents and office staff, set
 * their role and permission preset. Reuses the existing live anchors on the
 * page (``page-admin-users`` / ``admin-users-email-suppression`` /
 * ``admin-users-add``) -- a page-scoped sibling of the broader admin-users
 * SECTION tour launched from Training.
 */
export const ADMIN_USER_MANAGEMENT_TOUR: TourDefinition = {
  id: "admin-user-management-page",
  title: "User Management page tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/users",
      target: "page-admin-users",
      section: "Welcome",
      title: "Your people",
      body: "User Management is where you invite your agents and office staff and decide what each of them can see and do. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "email-suppression",
      route: "/admin/users",
      target: "admin-users-email-suppression",
      section: "Email status",
      title: "Outbound email status",
      body: "Inviting someone here sends them an email with a link to set up their account. If you see an amber notice here, outbound email is currently switched off for testing -- an invite is still created but the email is not actually sent, and you are given a one-time setup link to copy and pass on yourself instead. Once your workspace is live, invites and other automated emails are delivered by real email.",
      placement: "bottom",
    },
    {
      // Add a deal on behalf (#2194, REQ D4-ONB-015). Matt's path: a super-admin
      // can load a deal for an agent who is away straight from User Management.
      // Anchored on the always-rendered page region (the per-row blue add-deal
      // icon is data-dependent, so the path is described in copy, not a brittle
      // per-row anchor). Gated on capture_on_behalf -- an admin always holds it,
      // so it shows here; the gate documents the capability + keeps the step out
      // of any non-capturing viewer. The shared "Capturing for agent" picker step
      // lives on the paralegal/manager WELCOME tours (a single-route admin PAGE
      // tour never leaves /admin/users); here the picker is covered in copy.
      id: "capture-on-behalf",
      route: "/admin/users",
      target: "page-admin-users",
      section: "Add a deal on behalf",
      permFlag: "capture_on_behalf",
      title: "Capture for an agent who is away",
      body: "When an agent is off sick or away, you can load a deal or a new listing on their behalf. On an active agent's row, the blue capture icon offers Add Sale, Add Lease and Add Listing, each opening the form already set to capture for that agent -- the work and its commission are credited to them, not to you.",
      placement: "center",
    },
    {
      id: "invite",
      route: "/admin/users",
      target: "admin-users-add",
      kind: "action",
      section: "Invite",
      title: "Invite a person",
      body: "Add User invites a new agent or staff member: capture their name and email, pick a role and a permission preset, and the system sends a set-password link -- they set their own password on first sign in. The preset shapes the workspace to the person. That is User Management.",
      placement: "left",
    },
  ],
};

/**
 * Contact Deduplication (``/admin/contacts/dedup``) -- the admin-only review
 * queue for merging likely-duplicate contacts.
 */
export const ADMIN_CONTACT_DEDUP_TOUR: TourDefinition = {
  id: "admin-contact-dedup-page",
  title: "Duplicate Contacts tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/contacts/dedup",
      target: "page-admin-contact-dedup",
      section: "Welcome",
      title: "Tidy your contacts",
      body: "Duplicate Contacts surfaces likely-duplicate records so you can merge them into one. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "signal",
      route: "/admin/contacts/dedup",
      target: "admin-contact-dedup-signal",
      section: "Match signal",
      title: "Group by how they match",
      body: "Filter the candidate groups by the signal that flagged them -- the same ID number, the same email, or a matching name and cell -- so you can review the strongest matches first.",
      placement: "bottom",
    },
    {
      id: "merge",
      route: "/admin/contacts/dedup",
      target: "page-admin-contact-dedup",
      section: "Merge",
      title: "Keep one, merge the rest",
      body: "In each group you choose the contact to keep; every linked deal, listing, note and document moves to it, and the others are archived -- reversible, so nothing is lost. There is a preview before you confirm. That is Duplicate Contacts.",
      placement: "center",
    },
  ],
};

/**
 * Integrations (``/admin/integrations``) -- the admin BYOK settings page for
 * connecting your own third-party services: one card each for the listings feed
 * (PROP Data), the property-data / deeds provider (LOOM), FICA verification
 * (VerifyNow) and the WhatsApp-lead provider (Sudonum).
 */
export const ADMIN_INTEGRATIONS_TOUR: TourDefinition = {
  id: "admin-integrations-page",
  title: "Integrations tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/integrations",
      target: "page-admin-integrations",
      section: "Welcome",
      title: "Connect your services",
      body: "Integrations is where you connect your agency's own third-party services. Each has its own card: PROP Data for your listings feed, LOOM for property and ownership reports, VerifyNow for FICA identity checks, and Sudonum for WhatsApp leads and the lead-response score. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "bottom",
    },
    {
      id: "byok",
      route: "/admin/integrations",
      target: "page-admin-integrations",
      section: "Your key",
      title: "Bring your own key",
      body: "Each card takes your own account credentials. They are stored encrypted and shown only as the last few characters afterwards -- a secret is never displayed again. You switch each integration on or off from its own card, and a service you have not connected simply stays off.",
      placement: "center",
    },
    {
      id: "test",
      route: "/admin/integrations",
      target: "page-admin-integrations",
      section: "Test",
      title: "Test before you rely on it",
      body: "Use Test connection on a card to confirm its credentials work without changing any data. When an integration is on, it runs quietly in the background. That is Integrations.",
      placement: "center",
    },
  ],
};

/**
 * Commission Sheet (``/admin/commission-sheet``) -- the finance/super-user
 * monthly commission workbook: one row per deal with splits, referral, company
 * net, VAT, monies received and free-form deduction lines, filterable by month /
 * financial year with PDF + Excel export. A money page, so it gates on the
 * financial module (audience + every step).
 */
export const ADMIN_COMMISSION_SHEET_TOUR: TourDefinition = {
  id: "admin-commission-sheet-page",
  title: "Commission Sheet tour",
  audience: { adminOnly: true, module: "financial" },
  steps: [
    {
      id: "welcome",
      route: "/admin/commission-sheet",
      target: "center",
      section: "Welcome",
      module: "financial",
      title: "Your monthly commission workbook",
      body: "The Commission Sheet is the monthly commission workbook in one place -- a row per deal showing the agent splits, any referral, the company's net portion, VAT, what has been received and any deductions. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "overview",
      route: "/admin/commission-sheet",
      target: "page-commission-sheet",
      section: "Overview",
      module: "financial",
      title: "One row per deal",
      body: "Each deal reads left to right: the agents and their splits, the referral, the company net, the total excluding and including VAT, whether the money is in, and any deduction lines you add. Export the whole sheet to PDF or Excel for your records.",
      placement: "bottom",
    },
    {
      id: "period",
      route: "/admin/commission-sheet",
      target: "commission-sheet-period",
      section: "Period",
      module: "financial",
      title: "Choose the period",
      body: "Pick a single month or the whole financial year, and the sheet rebuilds to just those deals. That is the Commission Sheet.",
      placement: "bottom",
    },
  ],
};

/**
 * KPI Monthly Upload (``/admin/kpi-upload``) -- where the admin opens a
 * reporting month and uploads the month's activity files for scoring. No money
 * gate (activity, not commission), admin-only.
 */
export const ADMIN_KPI_UPLOAD_TOUR: TourDefinition = {
  id: "admin-kpi-upload-page",
  title: "KPI Monthly Upload tour",
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/kpi-upload",
      target: "center",
      section: "Welcome",
      title: "Upload the month's activity",
      body: "KPI Monthly Upload is where you bring in each month's activity files so every agent can be scored. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "overview",
      route: "/admin/kpi-upload",
      target: "page-kpi-upload",
      section: "Overview",
      title: "Files in, columns detected",
      body: "Open a reporting month, then upload the activity files. The column names are detected for you and remembered for next month, so each upload gets faster.",
      placement: "bottom",
    },
    {
      // The reporting-month picker only renders once at least one month has been
      // opened; on a brand-new agency with no months yet the anchor is absent and
      // the engine simply auto-advances past it (TARGET_NOT_FOUND, the documented
      // #1959 behaviour) -- never a broken tour.
      id: "month",
      route: "/admin/kpi-upload",
      target: "kpi-upload-month",
      section: "Reporting month",
      title: "Pick the reporting month",
      body: "Choose which reporting month you are uploading for, then stage each file. Once every file is in and looks right, the month is ready for scoring. That is the monthly upload.",
      placement: "bottom",
    },
  ],
};

/**
 * Agent Compliance roster in the ADMIN shell (``/admin/compliance/agents``).
 *
 * The SAME ``AgentComplianceRoster`` page is mounted at both
 * ``/admin/compliance/agents`` and ``/crm/compliance/agents`` with the same
 * ``data-tour`` anchors. The back-office page tour routes every step to the CRM
 * mount, so wiring it to the admin route hid the launcher there (its opening
 * route did not match) and a Training launch dropped an admin out of the admin
 * shell. This admin variant clones those steps onto the admin mount and is
 * audience-gated to admins; the CRM tour keeps serving HR (``manage_compliance``).
 */
export const ADMIN_AGENT_COMPLIANCE_PAGE_TOUR: TourDefinition = {
  id: "admin-agent-compliance-page",
  title: BACK_OFFICE_AGENT_COMPLIANCE_PAGE_TOUR.title,
  audience: { adminOnly: true },
  steps: BACK_OFFICE_AGENT_COMPLIANCE_PAGE_TOUR.steps.map((s) => ({
    ...s,
    route: "/admin/compliance/agents",
  })),
};

/**
 * Read-only OVERSIGHT variant of an admin page tour (#2452). A manager
 * (view_management_dashboards) reaches the SAME dashboard component mounted at
 * `/crm/oversight/<x>` (App.tsx keep-in-shell mounts) -- same page, same
 * `data-tour` anchors -- but must NOT be navigated to the `/admin/<x>` route
 * (AdminRoute would 403). This clones a base admin PAGE tour, remaps every
 * step's route to the oversight mount, and re-audiences it to the management
 * flag (admins hold that flag too, but they use the /admin/* tour on the
 * /admin route). Any `kind: "action"` step is dropped -- oversight is
 * read-only, so it never tells a manager to press an edit/upload control.
 */
function makeOversightVariant(
  base: TourDefinition,
  route: string,
  idSuffix: string,
  overrides?: Record<string, Partial<Pick<TourStep, "title" | "body">>>,
): TourDefinition {
  return {
    id: `oversight-${idSuffix}`,
    title: base.title,
    audience: { flag: "view_management_dashboards" },
    steps: base.steps
      .filter((s) => s.kind !== "action")
      .map((s) => ({ ...s, route, ...(overrides?.[s.id] ?? {}) })),
  };
}

/**
 * The manager read-only OVERSIGHT page-tour variants (#2452). Each mirrors an
 * admin page tour onto the `/crm/oversight/*` keep-in-shell mount and is
 * audience-gated to `view_management_dashboards`. Spread into TOUR_REGISTRY
 * (registry.ts) and route-wired in routeTours.ts. NB these are a SEPARATE export
 * from ADMIN_PAGE_TOURS -- they are not admin-audience-gated.
 */
export const OVERSIGHT_PAGE_TOURS: TourDefinition[] = [
  makeOversightVariant(ADMIN_MONTHLY_DEALS_TOUR, "/crm/oversight/monthly-deals", "monthly-deals"),
  makeOversightVariant(ADMIN_DEAL_PIPELINE_TOUR, "/crm/oversight/deal-pipeline", "deal-pipeline"),
  makeOversightVariant(ADMIN_ATTORNEY_PIPELINE_TOUR, "/crm/oversight/attorney-pipeline", "attorney-pipeline"),
  makeOversightVariant(ADMIN_FICA_PAGE_TOUR, "/crm/oversight/compliance", "compliance"),
  makeOversightVariant(ADMIN_KPI_REPORT_TOUR, "/crm/oversight/kpis", "kpis", {
    config: {
      body: "The metrics, points and tiers behind these scores are set under Settings by the office -- your scores follow whatever is configured there. That is KPI Reports.",
    },
  }),
  makeOversightVariant(ADMIN_MARKET_SHARE_TOUR, "/crm/oversight/market-share", "market-share"),
  makeOversightVariant(ADMIN_COMMUNICATIONS_TOUR, "/crm/oversight/communications", "communications", {
    filters: {
      body: "Filter by agent, channel, direction, status or date range to find any message fast, and see at a glance which ones failed to deliver. That is Communications.",
    },
  }),
];

/**
 * Every per-page admin tour, in admin-shell order. Spread into TOUR_REGISTRY
 * (registry.ts) and route-wired in routeTours.ts.
 */
export const ADMIN_PAGE_TOURS: TourDefinition[] = [
  ADMIN_COMMISSION_PAGE_TOUR,
  ADMIN_SALES_TARGETS_TOUR,
  ADMIN_LEADERBOARD_TOUR,
  ADMIN_MONTHLY_DEALS_TOUR,
  ADMIN_MARKET_SHARE_TOUR,
  ADMIN_DEAL_PIPELINE_TOUR,
  ADMIN_ATTORNEY_PIPELINE_TOUR,
  ADMIN_KPI_REPORT_TOUR,
  ADMIN_KPI_METRICS_TOUR,
  ADMIN_KPI_TIERS_TOUR,
  ADMIN_FICA_PAGE_TOUR,
  ADMIN_COMMUNICATIONS_TOUR,
  ADMIN_USER_MANAGEMENT_TOUR,
  ADMIN_CONTACT_DEDUP_TOUR,
  ADMIN_INTEGRATIONS_TOUR,
  ADMIN_COMMISSION_SHEET_TOUR,
  ADMIN_KPI_UPLOAD_TOUR,
  ADMIN_AGENT_COMPLIANCE_PAGE_TOUR,
];
